import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getTodasAreas, getTodosClientes, getServiciosPorAreaCliente, updateCliente, addServicioACliente, getClientePorId } from '../../actions/index.js';
import Select from 'react-select';
import swal from 'sweetalert';
import { logger } from '../../utils/logger.js';
import './EditarCliente.css';

// react-select con la paleta moderna oscura + verde (sirve para single y multi)
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: '#0A100D',
    borderColor: state.isFocused ? 'rgba(40,199,111,0.6)' : 'rgba(255,255,255,0.10)',
    borderWidth: '0.5px', borderRadius: '10px', color: '#E8EFEA', minHeight: '42px',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(40,199,111,0.20)' : 'none',
    '&:hover': { borderColor: 'rgba(40,199,111,0.45)' }
  }),
  menu: (provided) => ({ ...provided, backgroundColor: '#0C120F', border: '0.5px solid rgba(40,199,111,0.25)', borderRadius: '11px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.6)', zIndex: 9999 }),
  menuList: (provided) => ({ ...provided, padding: '6px', maxHeight: '230px' }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? 'rgba(40,199,111,0.16)' : state.isFocused ? 'rgba(40,199,111,0.12)' : 'transparent',
    color: state.isSelected || state.isFocused ? '#EAF6EF' : '#CBD6CF', borderRadius: '8px', padding: '9px 11px', cursor: 'pointer',
  }),
  singleValue: (provided) => ({ ...provided, color: '#E8EFEA', fontSize: 13 }),
  placeholder: (provided) => ({ ...provided, color: '#9FB0A6', fontSize: 13 }),
  input: (provided) => ({ ...provided, color: '#E8EFEA' }),
  valueContainer: (provided) => ({ ...provided, padding: '4px 8px', gap: '4px' }),
  multiValue: (provided) => ({ ...provided, backgroundColor: 'rgba(40,199,111,0.14)', borderRadius: '999px' }),
  multiValueLabel: (provided) => ({ ...provided, color: '#3FE07F', fontSize: 12, padding: '2px 4px 2px 8px' }),
  multiValueRemove: (provided) => ({ ...provided, color: '#3FE07F', borderRadius: '999px', ':hover': { backgroundColor: 'rgba(40,199,111,0.25)', color: '#EAF6EF' } }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (provided, state) => ({ ...provided, color: state.isFocused ? '#3FE07F' : '#5C6862', ':hover': { color: '#3FE07F' } }),
  clearIndicator: (provided) => ({ ...provided, color: '#9FB0A6', ':hover': { color: '#E86D6D' } }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const EditarClienteSimple = ({ onClose, onClienteActualizado }) => {
  const dispatch = useDispatch();
  const areas = useSelector(state => state.Repoteareas);
  const clientesReporte = useSelector(state => state.clientesReporte);

  // Estados principales
  const [paso, setPaso] = useState(1); // 1: Seleccionar, 2: Editar datos, 3: Gestionar áreas
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [loading, setLoading] = useState(false);

  // Estados para datos del cliente
  const [datosCliente, setDatosCliente] = useState({
    nombre: '',
    descripcion: '',
    correo: '',
    numeroContacto: '',
    direccion: '',
    estado: true
  });

  // Estados para mostrar información actual
  const [areasActuales, setAreasActuales] = useState([]);
  const [serviciosActuales, setServiciosActuales] = useState([]);

  // Estados para agregar nuevas áreas/servicios
  const [mostrarAgregarAreas, setMostrarAgregarAreas] = useState(false);
  const [mostrarAgregarServicios, setMostrarAgregarServicios] = useState(false);
  const [mostrarAgregarServiciosExistentes, setMostrarAgregarServiciosExistentes] = useState(false);
  const [areasParaAgregar, setAreasParaAgregar] = useState([]);
  const [serviciosDisponibles, setServiciosDisponibles] = useState([]);
  const [serviciosParaAgregar, setServiciosParaAgregar] = useState([]);
  const [serviciosExistentesDisponibles, setServiciosExistentesDisponibles] = useState([]);
  const [serviciosExistentesParaAgregar, setServiciosExistentesParaAgregar] = useState([]);

  // Extraer arrays
  const areasArray = areas?.areas || [];
  const clientesArray = Array.isArray(clientesReporte) ? clientesReporte : [];

  // Cargar datos iniciales
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        logger.log('🔄 Cargando datos iniciales EditarClienteSimple...');
        await Promise.all([
          dispatch(getTodosClientes()),
          dispatch(getTodasAreas())
        ]);
        logger.log('✅ Datos iniciales cargados');
      } catch (error) {
        logger.error('❌ Error al cargar datos iniciales:', error);
      }
    };
    cargarDatos();
  }, [dispatch]);

  // Debug logs
  useEffect(() => {
    logger.log('🔍 Estado clientesReporte:', clientesReporte);
    logger.log('🔍 Array de clientes:', clientesArray);
    logger.log('🔍 Opciones clientes:', clientesArray.map(c => ({ id: c.id, nombre: c.nombre })));
  }, [clientesReporte, clientesArray]);

  // Opciones para selects
  const opcionesClientes = clientesArray.map(cliente => ({
    value: cliente.id,
    label: `${cliente.nombre} ${cliente.estado ? '(Activo)' : '(Inactivo)'}`,
    cliente: cliente
  }));

  // Áreas disponibles para agregar (excluyendo las que ya tiene)
  const opcionesAreasParaAgregar = areasArray.filter(area => 
    !areasActuales.some(areaActual => areaActual.id === area.id || areaActual.nombre === area.nombre)
  ).map(area => ({
    value: area.id,
    label: area.nombre
  }));

  // Seleccionar cliente
  const handleClienteChange = async (selectedOption) => {
    if (!selectedOption) {
      setClienteSeleccionado(null);
      setAreasActuales([]);
      setServiciosActuales([]);
      setPaso(1);
      return;
    }

    const clienteBasico = selectedOption.cliente;
    logger.log('🔍 Cliente básico seleccionado:', clienteBasico);
    
    // Obtener cliente completo con áreas y servicios
    try {
      logger.log('� Obteniendo cliente completo...');
      const clienteCompleto = await dispatch(getClientePorId(clienteBasico.id));
      logger.log('🔍 Cliente completo obtenido:', clienteCompleto);
      
      setClienteSeleccionado(clienteCompleto);
      
      logger.log('🔍 Áreas del cliente completo:', clienteCompleto.areas);
      logger.log('🔍 Servicios del cliente completo:', clienteCompleto.servicios);
      logger.log('🔍 Array de áreas disponibles:', areasArray);
      
      // Cargar datos básicos
      setDatosCliente({
        nombre: clienteCompleto.nombre || '',
        descripcion: clienteCompleto.descripcion || '',
        correo: clienteCompleto.correo || '',
        numeroContacto: clienteCompleto.numeroContacto || '',
        direccion: clienteCompleto.direccion || '',
        estado: clienteCompleto.estado !== undefined ? clienteCompleto.estado : true
      });

      // Procesar áreas actuales
      const clienteAreas = clienteCompleto.areas || [];
      logger.log('🔍 Áreas del cliente después de || []:', clienteAreas);
      
      const areasCompletas = clienteAreas.map(area => {
        logger.log('🔍 Procesando área:', area, 'tipo:', typeof area);
        if (typeof area === 'string') {
          const areaEncontrada = areasArray.find(a => a.nombre === area);
          logger.log('🔍 Área string encontrada:', areaEncontrada);
          return areaEncontrada || { nombre: area, id: area };
        } else if (typeof area === 'object' && area.id) {
          logger.log('🔍 Área es objeto con ID:', area);
          return area;
        } else if (typeof area === 'number') {
          const areaEncontrada = areasArray.find(a => a.id === area);
          logger.log('🔍 Área número encontrada:', areaEncontrada);
          return areaEncontrada || { nombre: `ID: ${area}`, id: area };
        }
        logger.log('🔍 Área otro tipo, creando objeto:', area);
        return { nombre: area, id: area };
      });
      
      logger.log('🔍 Áreas completas procesadas:', areasCompletas);
      setAreasActuales(areasCompletas);
      
      // Servicios actuales
      const serviciosActuales = clienteCompleto.servicios || [];
      logger.log('🔍 Servicios actuales procesados:', serviciosActuales);
      setServiciosActuales(serviciosActuales);
      
      setPaso(2);
      logger.log('✅ Cliente completo cargado:', clienteCompleto);
      
    } catch (error) {
      logger.error('❌ Error al obtener cliente completo:', error);
      // Fallback: usar cliente básico
      logger.log('🔄 Usando cliente básico como fallback');
      
      setClienteSeleccionado(clienteBasico);
      
      // Cargar datos básicos del cliente fallback
      setDatosCliente({
        nombre: clienteBasico.nombre || '',
        descripcion: clienteBasico.descripcion || '',
        correo: clienteBasico.correo || '',
        numeroContacto: clienteBasico.numeroContacto || '',
        direccion: clienteBasico.direccion || '',
        estado: clienteBasico.estado !== undefined ? clienteBasico.estado : true
      });

      setAreasActuales([]);
      setServiciosActuales([]);
      setPaso(2);
    }
  };

  // Crear opciones agrupadas por área para servicios (copiado de CrearCliente)
  const crearOpcionesServiciosAgrupadas = (serviciosLista, filtrarExistentes = false) => {
    if (!serviciosLista || serviciosLista.length === 0) {
      return [];
    }

    // Si filtrarExistentes es true, excluir servicios ya asignados
    let serviciosFiltrados = serviciosLista;
    if (filtrarExistentes && serviciosActuales.length > 0) {
      const idsServiciosActuales = serviciosActuales.map(s => s.id);
      serviciosFiltrados = serviciosLista.filter(servicio => 
        !idsServiciosActuales.includes(servicio.id)
      );
      logger.log('🔍 Servicios filtrados (sin asignados):', serviciosFiltrados.length, 'de', serviciosLista.length);
    }

    // CASO: Los servicios son objetos completos
    if (typeof serviciosFiltrados[0] === 'object' && serviciosFiltrados[0].id) {
      logger.log('🔍 Los servicios son objetos completos');
      
      const serviciosPorArea = serviciosFiltrados.reduce((acc, servicio) => {
        logger.log('🔍 Analizando servicio para agrupamiento:', servicio);
        
        let nombreArea = 'Sin área';
        
        // Prioridad 1: Propiedad 'area' directa (nombre del área)
        if (servicio.area && typeof servicio.area === 'string') {
          nombreArea = servicio.area;
          logger.log('🔍 Área encontrada en servicio.area:', nombreArea);
        } 
        // Prioridad 2: Objeto Area con nombre
        else if (servicio.Area && servicio.Area.nombre) {
          nombreArea = servicio.Area.nombre;
          logger.log('🔍 Área encontrada en servicio.Area.nombre:', nombreArea);
        } 
        // Prioridad 3: Objeto area con nombre (minúscula)
        else if (servicio.area && servicio.area.nombre) {
          nombreArea = servicio.area.nombre;
          logger.log('🔍 Área encontrada en servicio.area.nombre:', nombreArea);
        }
        // Prioridad 4: ID de área para buscar en areasArray
        else if (servicio.areaId || servicio.area_id) {
          const areaId = servicio.areaId || servicio.area_id;
          const area = areasArray.find(a => a.id === areaId);
          nombreArea = area ? area.nombre : `Área ID: ${areaId}`;
          logger.log('🔍 Área encontrada por ID:', areaId, '→', nombreArea);
        }
        
        if (!acc[nombreArea]) {
          acc[nombreArea] = [];
        }
        
        acc[nombreArea].push({
          value: servicio.id,
          label: servicio.nombre,
          area: nombreArea
        });
        
        logger.log(`🔍 Servicio "${servicio.nombre}" → Área "${nombreArea}"`);
        return acc;
      }, {});

      logger.log('🔍 Servicios agrupados por área:', serviciosPorArea);

      // Convertir a formato de grupos para react-select
      return Object.keys(serviciosPorArea).map(nombreArea => ({
        label: nombreArea,
        options: serviciosPorArea[nombreArea].sort((a, b) => a.label.localeCompare(b.label))
      })).sort((a, b) => a.label.localeCompare(b.label));
    }

    return [];
  };

  // Cargar servicios de áreas existentes del cliente
  const cargarServiciosAreasExistentes = async () => {
    try {
      logger.log('🔄 Cargando servicios de áreas existentes...');
      
      if (!areasActuales || areasActuales.length === 0) {
        logger.log('⚠️ No hay áreas actuales para cargar servicios');
        return;
      }

      const areaIds = areasActuales.map(area => area.id);
      logger.log('🔍 IDs de áreas actuales:', areaIds);

      // Obtener servicios de estas áreas
      const servicios = await dispatch(getServiciosPorAreaCliente(clienteSeleccionado.id));
      logger.log('🔍 Servicios obtenidos para áreas existentes:', servicios);
      
      // Crear opciones agrupadas, filtrando servicios ya asignados
      const opcionesAgrupadas = crearOpcionesServiciosAgrupadas(servicios || [], true);
      setServiciosExistentesDisponibles(opcionesAgrupadas);
      
      // Verificar si hay servicios disponibles después del filtrado
      const totalServiciosDisponibles = opcionesAgrupadas.reduce((total, grupo) => total + grupo.options.length, 0);
      
      if (totalServiciosDisponibles === 0) {
        swal("Información", "El cliente ya tiene asignados todos los servicios disponibles para sus áreas.", "info");
        return;
      }
      
      setMostrarAgregarServiciosExistentes(true);
    } catch (error) {
      logger.error('Error al cargar servicios de áreas existentes:', error);
      swal("Error", "No se pudieron cargar los servicios", "error");
    }
  };

  // Manejar cambios en datos básicos
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDatosCliente(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Agregar áreas al cliente
  const handleAgregarAreas = async () => {
    if (areasParaAgregar.length === 0) {
      swal("Error", "Seleccione al menos un área para agregar", "error");
      return;
    }

    setLoading(true);
    try {
      // Obtener IDs de áreas actuales + nuevas
      const areasActualesIds = areasActuales.map(a => a.id);
      const todasLasAreasIds = [...areasActualesIds, ...areasParaAgregar];
      
      // Obtener IDs de servicios actuales para preservarlos
      const serviciosActualesIds = serviciosActuales.map(s => s.id);
      
      const resultado = await dispatch(updateCliente(
        clienteSeleccionado.id,
        datosCliente.nombre,
        datosCliente.correo,
        datosCliente.numeroContacto,
        datosCliente.direccion,
        datosCliente.estado,
        todasLasAreasIds,
        serviciosActualesIds // Preservar servicios existentes
      ));

      if (resultado) {
        // Actualizar áreas actuales
        const nuevasAreasCompletas = areasParaAgregar.map(areaId => {
          return areasArray.find(a => a.id === areaId);
        });
        setAreasActuales([...areasActuales, ...nuevasAreasCompletas]);
        
        // Limpiar selección
        setAreasParaAgregar([]);
        setMostrarAgregarAreas(false);
        
        swal("¡Éxito!", "Áreas agregadas correctamente. Use el botón 'Agregar Servicios' para agregar servicios.", "success");
      }
    } catch (error) {
      logger.error('Error al agregar áreas:', error);
      swal("Error", "No se pudieron agregar las áreas", "error");
    } finally {
      setLoading(false);
    }
  };

  // Agregar servicios al cliente
  const handleAgregarServicios = async () => {
    if (serviciosParaAgregar.length === 0) {
      swal("Error", "Seleccione al menos un servicio para agregar", "error");
      return;
    }

    setLoading(true);
    try {
      const resultado = await dispatch(addServicioACliente(
        clienteSeleccionado.id,
        serviciosParaAgregar
      ));

      if (resultado) {
        // Actualizar servicios actuales
        const nuevosServiciosCompletos = serviciosParaAgregar.map(servicioId => {
          return serviciosDisponibles.find(s => s.id === servicioId || s.nombre === servicioId) || servicioId;
        });
        setServiciosActuales([...serviciosActuales, ...nuevosServiciosCompletos]);
        
        // Limpiar selección
        setServiciosParaAgregar([]);
        setMostrarAgregarServicios(false);
        
        swal("¡Éxito!", "Servicios agregados correctamente", "success");
      }
    } catch (error) {
      logger.error('Error al agregar servicios:', error);
      swal("Error", "No se pudieron agregar los servicios", "error");
    } finally {
      setLoading(false);
    }
  };

  // Agregar servicios de áreas existentes al cliente
  const handleAgregarServiciosExistentes = async () => {
    if (serviciosExistentesParaAgregar.length === 0) {
      swal("Error", "Seleccione al menos un servicio para agregar", "error");
      return;
    }

    setLoading(true);
    try {
      const resultado = await dispatch(addServicioACliente(
        clienteSeleccionado.id,
        serviciosExistentesParaAgregar
      ));

      if (resultado) {
        // Actualizar servicios actuales
        const nuevosServiciosObjetos = serviciosExistentesParaAgregar.map(servicioId => {
          // Buscar en todas las opciones agrupadas
          for (let grupo of serviciosExistentesDisponibles) {
            const servicioEncontrado = grupo.options.find(s => s.value === servicioId);
            if (servicioEncontrado) {
              return {
                id: servicioEncontrado.value,
                nombre: servicioEncontrado.label,
                area: servicioEncontrado.area
              };
            }
          }
          return { id: servicioId, nombre: `Servicio ${servicioId}`, area: 'Desconocida' };
        });
        
        setServiciosActuales([...serviciosActuales, ...nuevosServiciosObjetos]);
        
        // Limpiar selección
        setServiciosExistentesParaAgregar([]);
        setMostrarAgregarServiciosExistentes(false);
        
        swal("¡Éxito!", "Servicios agregados correctamente", "success");
      }
    } catch (error) {
      logger.error('Error al agregar servicios existentes:', error);
      swal("Error", "No se pudieron agregar los servicios", "error");
    } finally {
      setLoading(false);
    }
  };

  // Guardar cambios en datos básicos
  const handleGuardarDatos = async (e) => {
    e.preventDefault();
    
    if (!datosCliente.nombre.trim()) {
      swal("Error", "El nombre del cliente es obligatorio", "error");
      return;
    }

    setLoading(true);
    try {
      const areasIds = areasActuales.map(a => a.id);
      const resultado = await dispatch(updateCliente(
        clienteSeleccionado.id,
        datosCliente.nombre,
        datosCliente.correo,
        datosCliente.numeroContacto,
        datosCliente.direccion,
        datosCliente.estado,
        areasIds,
        [] // Servicios se manejan por separado
      ));

      if (resultado) {
        swal("¡Éxito!", "Datos del cliente actualizados correctamente", "success");
        if (onClienteActualizado) onClienteActualizado(resultado);
      }
    } catch (error) {
      logger.error('Error al actualizar cliente:', error);
      swal("Error", "No se pudieron actualizar los datos del cliente", "error");
    } finally {
      setLoading(false);
    }
  };

  // Funciones auxiliares para mostrar nombres
  const obtenerNombresAreas = (areas) => {
    logger.log('🔍 obtenerNombresAreas recibió:', areas);
    if (!areas || areas.length === 0) {
      logger.log('🔍 obtenerNombresAreas: sin áreas, retornando "Ninguna"');
      return 'Ninguna';
    }
    const nombres = areas.map(area => area.nombre || area).join(', ');
    logger.log('🔍 obtenerNombresAreas resultado:', nombres);
    return nombres;
  };

  const obtenerNombresServicios = (servicios) => {
    logger.log('🔍 obtenerNombresServicios recibió:', servicios);
    if (!servicios || servicios.length === 0) {
      logger.log('🔍 obtenerNombresServicios: sin servicios, retornando "Ninguno"');
      return 'Ninguno';
    }
    const nombres = servicios.map(servicio => servicio.nombre || servicio).join(', ');
    logger.log('🔍 obtenerNombresServicios resultado:', nombres);
    return nombres;
  };

  return (
    <div className="editar-cliente-modal">
      <div className="editar-cliente-container">
        <div className="editar-cliente-header">
          <h2>
            {paso === 1 && 'Editar Cliente - Seleccionar Cliente'}
            {paso === 2 && `Editar Cliente - ${clienteSeleccionado?.nombre}`}
          </h2>
          <button className="btn-close" onClick={onClose}>
            <img src={require('../../images/cruz-verde.svg').default} alt="Cerrar" />
          </button>
        </div>

        <div className="editar-cliente-content">
          {paso === 1 && (
            <div className="paso-1">
              <h3>Seleccione el cliente a editar</h3>
              <Select
                options={opcionesClientes}
                onChange={handleClienteChange}
                placeholder="Busque y seleccione el cliente..."
                isSearchable
                className="react-select-container"
                classNamePrefix="repms"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={customSelectStyles}
              />
            </div>
          )}

          {paso === 2 && clienteSeleccionado && (
            <div className="paso-2">
              {/* Datos básicos del cliente */}
              <form onSubmit={handleGuardarDatos}>
                <div className="form-section">
                  <h4>Datos Básicos</h4>
                  
                  <div className="form-group">
                    <label>Nombre del Cliente *</label>
                    <input
                      type="text"
                      name="nombre"
                      value={datosCliente.nombre}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Descripción</label>
                    <textarea
                      name="descripcion"
                      value={datosCliente.descripcion}
                      onChange={handleInputChange}
                      rows="3"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Correo Electrónico</label>
                      <input
                        type="email"
                        name="correo"
                        value={datosCliente.correo}
                        onChange={handleInputChange}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Número de Contacto</label>
                      <input
                        type="text"
                        name="numeroContacto"
                        value={datosCliente.numeroContacto}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Dirección</label>
                    <input
                      type="text"
                      name="direccion"
                      value={datosCliente.direccion}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="estado"
                        checked={datosCliente.estado}
                        onChange={handleInputChange}
                      />
                      Cliente Activo
                    </label>
                  </div>

                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'Guardando...' : 'Guardar Datos'}
                  </button>
                </div>
              </form>

              {/* Áreas actuales */}
              <div className="form-section">
                <h4>Gestión de Áreas</h4>
                <p className="info-text">
                  <strong>Áreas actuales:</strong> {obtenerNombresAreas(areasActuales)}
                </p>
                
                {!mostrarAgregarAreas ? (
                  <button 
                    className="btn-secondary"
                    onClick={() => setMostrarAgregarAreas(true)}
                  >
                    Agregar Nuevas Áreas
                  </button>
                ) : (
                  <div className="agregar-areas">
                    <label>Seleccionar Áreas para Agregar</label>
                    <Select
                      isMulti
                      options={opcionesAreasParaAgregar}
                      onChange={(selected) => setAreasParaAgregar(selected ? selected.map(s => s.value) : [])}
                      placeholder="Seleccione áreas..."
                      className="react-select-container"
                      classNamePrefix="repms"
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={customSelectStyles}
                    />
                    <div className="button-group">
                      <button 
                        className="btn-primary"
                        onClick={handleAgregarAreas}
                        disabled={loading || areasParaAgregar.length === 0}
                      >
                        {loading ? 'Agregando...' : 'Agregar Áreas'}
                      </button>
                      <button 
                        className="btn-cancel"
                        onClick={() => {
                          setMostrarAgregarAreas(false);
                          setAreasParaAgregar([]);
                        }}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Servicios actuales */}
              <div className="form-section">
                <h4>Gestión de Servicios</h4>
                <p className="info-text">
                  <strong>Servicios actuales:</strong> {obtenerNombresServicios(serviciosActuales)}
                </p>
                
                <div className="buttons-row">
                  <button
                    type="button"
                    onClick={cargarServiciosAreasExistentes}
                    className="btn btn-secondary"
                    disabled={loading || !areasActuales.length}
                    title={!areasActuales.length ? "Primero debe tener áreas asignadas" : "Agregar servicios de las áreas actuales"}
                  >
                    Agregar Servicios
                  </button>
                </div>
                
                {mostrarAgregarServicios && serviciosDisponibles.length > 0 && (
                  <div className="agregar-servicios">
                    <p className="success-text">
                      ¡Áreas agregadas! Ahora puede seleccionar servicios de las nuevas áreas:
                    </p>
                    <label>Seleccionar Servicios para Agregar</label>
                    <Select
                      isMulti
                      options={serviciosDisponibles}
                      onChange={(selected) => {
                        const flatOptions = serviciosDisponibles.flatMap(grupo => 
                          grupo.options ? grupo.options : []
                        );
                        const valores = selected ? selected.map(s => s.value) : [];
                        setServiciosParaAgregar(valores);
                      }}
                      placeholder="Seleccione servicios agrupados por área..."
                      className="react-select-container"
                      classNamePrefix="repms"
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={customSelectStyles}
                    />
                    <div className="button-group">
                      <button 
                        className="btn-primary"
                        onClick={handleAgregarServicios}
                        disabled={loading || serviciosParaAgregar.length === 0}
                      >
                        {loading ? 'Agregando...' : 'Agregar Servicios'}
                      </button>
                      <button 
                        className="btn-cancel"
                        onClick={() => {
                          setMostrarAgregarServicios(false);
                          setServiciosParaAgregar([]);
                        }}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}

                {mostrarAgregarServiciosExistentes && serviciosExistentesDisponibles.length > 0 && (
                  <div className="agregar-servicios">
                    <p className="info-text">
                      Seleccione servicios adicionales de las áreas que ya tiene el cliente:
                    </p>
                    <label>Seleccionar Servicios para Agregar</label>
                    <Select
                      isMulti
                      options={serviciosExistentesDisponibles}
                      onChange={(selected) => {
                        const valores = selected ? selected.map(s => s.value) : [];
                        setServiciosExistentesParaAgregar(valores);
                      }}
                      placeholder="Seleccione servicios agrupados por área..."
                      className="react-select-container"
                      classNamePrefix="repms"
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={customSelectStyles}
                    />
                    <div className="button-group">
                      <button 
                        className="btn-primary"
                        onClick={handleAgregarServiciosExistentes}
                        disabled={loading || serviciosExistentesParaAgregar.length === 0}
                      >
                        {loading ? 'Agregando...' : 'Agregar Servicios'}
                      </button>
                      <button 
                        className="btn-cancel"
                        onClick={() => {
                          setMostrarAgregarServiciosExistentes(false);
                          setServiciosExistentesParaAgregar([]);
                        }}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button className="btn-cancel" onClick={onClose}>
                  Cerrar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


export default EditarClienteSimple;
